import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  ChevronDown,
  CircleAlert,
  Landmark,
  Loader2,
  Pencil,
  RefreshCw,
  Ban,
  ShieldCheck,
  Tags,
} from 'lucide-react';
import { formatDateBR, type ApproveOpenFinanceReviewResponse, type ApproveOpenFinanceSafeResponse, type OpenFinanceLaunch, type OpenFinanceQueueResponse, type OpenFinanceReviewItem, type SyncOpenFinanceResponse, type Ymd } from '@lira/shared';
import { ConfirmDialog, EmptyState, MoneyText, Skeleton, CategoryIcon } from '@/components/ui';
import { TransactionModal } from '@/features/transactions/TransactionModal';
import { PageHeader } from '@/components/layout/PageHeader';
import { apiGet, apiPatch, apiPost } from '@/lib/api';
import { cn } from '@/lib/cn';
import { errorMessage } from '@/lib/errorMessage';
import { boundedSyncProgress, bulkResultMessage, counterpartyTypeLabel, operationTypeLabel, safeReviewIds } from './review-queue';
import { toast } from '@/stores/toast';
import { institutionBrandSlug, institutionBrand } from './institution-brand';

const QUERY_KEY = ['open-finance', 'reviews'] as const;

type QueueGroup = OpenFinanceQueueResponse['groups'][number];
type QueueItem = QueueGroup['items'][number];
type EditTarget = { item: QueueItem; source: QueueGroup['source'] };
type ConfirmAction =
  | { kind: 'single'; item: QueueItem }
  | { kind: 'bulk'; reviewIds: string[]; totalSafe: number };

function newIdempotencyKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `of-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function QueueSkeleton() {
  return (
    <div className="space-y-4" aria-label="Carregando movimentações Open Finance">
      <Skeleton className="h-28 w-full" />
      <Skeleton className="h-52 w-full" />
    </div>
  );
}

function ReviewRow({ item, onApprove, onEdit, onIgnore, onSeen, approving }: {
  item: QueueItem;
  onApprove: () => void;
  onEdit: () => void;
  onIgnore: () => void;
  onSeen: () => void;
  approving: boolean;
}) {
  return (
    <li className="grid gap-3 border-t border-border px-4 py-4 transition-[background-color,transform] duration-200 first:border-t-0 hover:relative hover:z-10 hover:scale-[1.01] hover:bg-primary-weak sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-center sm:gap-5">
      <div className="min-w-0">
        <div className="flex min-w-0 items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium text-ink">{item.suggestedTitle}</p>
            <p className="mt-0.5 truncate text-xs text-muted" title={item.originalDescription}>
              Banco: {item.originalDescription}
            </p>
            {item.counterpartyName && <p className="mt-1 truncate text-xs font-medium text-ink">
              {item.amountCents < 0 ? 'Destinatário' : 'Origem'}: {item.counterpartyName}
            </p>}
          </div>
          {item.unseen && <button type="button" onClick={onSeen} className="inline-flex shrink-0 items-center rounded-full bg-primary-weak px-2 py-1 text-[11px] font-semibold text-primary" aria-label={`Marcar ${item.suggestedTitle} como visto`}>Novo</button>}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
          <span>{formatDateBR(item.suggestedDate)}</span>
          <span className="inline-flex items-center gap-1"><Tags className="size-3.5" />{item.suggestedCategoryName ?? 'Sem categoria'}</span>
          {item.specialType && <span className="rounded-full bg-primary-weak px-2 py-1 font-semibold text-primary">
            {item.specialType === 'card_payment' ? 'Pagamento de fatura' : 'Transferência'}
          </span>}
          {item.counterpartyType && <span className="rounded-full border border-border bg-surface px-2 py-1 font-semibold text-ink">
            {counterpartyTypeLabel(item.counterpartyType)}
          </span>}
          {item.operationType && <span className="rounded-full border border-border bg-surface px-2 py-1 font-semibold text-ink">
            {operationTypeLabel(item.operationType)}
          </span>}
        </div>
      </div>
      <MoneyText cents={item.amountCents} colorBySign showPositiveSign bold className="text-base sm:text-right" />
      <div className="flex flex-wrap gap-2 sm:justify-end">
        <button type="button" onClick={onEdit} className="flex h-11 items-center gap-1 rounded-full border border-border px-3 text-sm font-semibold text-ink hover:bg-canvas">
          <Pencil className="size-4" /> Editar
        </button>
        <button type="button" onClick={onIgnore} className="flex h-11 items-center gap-1 rounded-full border border-border px-3 text-sm font-semibold text-muted hover:bg-canvas">
          <Ban className="size-4" /> Ignorar
        </button>
        {item.safe && <button
          type="button"
          disabled={approving}
          onClick={onApprove}
          className="flex h-11 min-w-28 items-center justify-center gap-2 rounded-full bg-primary px-4 text-sm font-semibold text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {approving ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
          Aprovar
        </button>}
      </div>
    </li>
  );
}

function QueueGroupCard({ group, onApprove, onEdit, onIgnore, onSeen, approvingId }: {
  group: QueueGroup;
  onApprove: (item: QueueItem) => void;
  onEdit: (item: QueueItem) => void;
  onIgnore: (item: QueueItem) => void;
  onSeen: (item: QueueItem) => void;
  approvingId: string | null;
}) {
  const [expanded, setExpanded] = useState(true);
  const brandSlug = institutionBrandSlug(group.connection.institutionName);
  const brand = brandSlug ? institutionBrand(group.connection.institutionName) : undefined;
  const pendingTotal = group.summary.incomingCents + group.summary.outgoingCents;
  return (
    <section className="overflow-hidden rounded-[var(--radius-panel)] bg-surface shadow-card">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((open) => !open)}
        className="flex min-h-16 w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-canvas"
      >
        {brand && brandSlug
          ? <CategoryIcon icon={`brand:${brandSlug}`} color={brand.hex} size={40} />
          : <span className="grid size-10 shrink-0 place-items-center rounded-full bg-primary-weak text-primary"><Landmark className="size-5" /></span>}
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold text-ink">{group.connection.institutionName}</span>
          <span className="block truncate text-sm text-muted">{group.source.displayName} · {group.summary.pendingCount} pendente{group.summary.pendingCount === 1 ? '' : 's'}</span>
        </span>
        <span className="hidden text-right sm:block">
          <span className="block text-xs text-muted">Total pendente</span>
          <MoneyText cents={pendingTotal} colorBySign showPositiveSign bold className="text-sm" />
        </span>
        <ChevronDown className={cn('size-5 shrink-0 text-muted transition-transform', expanded && 'rotate-180')} />
      </button>
      {expanded && <ul>{group.items.map((item) => <ReviewRow key={item.id} item={item} approving={approvingId === item.id} onApprove={() => onApprove(item)} onEdit={() => onEdit(item)} onIgnore={() => onIgnore(item)} onSeen={() => onSeen(item)} />)}</ul>}
    </section>
  );
}

/** Inbox de revisão antes de qualquer lançamento externo alcançar o ledger. */
export function OpenFinanceReviewPage() {
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);
  const [editItem, setEditItem] = useState<EditTarget | null>(null);
  const [ignoreItem, setIgnoreItem] = useState<QueueItem | null>(null);
  const [syncSummary, setSyncSummary] = useState<string | null>(null);
  const [syncStartedAt, setSyncStartedAt] = useState<number | null>(null);
  const [syncProgress, setSyncProgress] = useState(0);
  useEffect(() => {
    if (syncStartedAt === null) return;
    const interval = window.setInterval(() => setSyncProgress(boundedSyncProgress(Date.now() - syncStartedAt)), 300);
    return () => window.clearInterval(interval);
  }, [syncStartedAt]);
  const queue = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => apiGet<OpenFinanceQueueResponse>('/open-finance/reviews'),
  });
  const approve = useMutation({
    mutationFn: (item: QueueItem) => {
      if (!item.launch) throw new Error('Defina o destino do lançamento antes de aprovar.');
      return apiPost<ApproveOpenFinanceReviewResponse>(`/open-finance/reviews/${item.id}/approve`, {
      revision: item.revision,
      expectedDate: item.suggestedDate,
      expectedTitle: item.suggestedTitle,
      expectedCategoryId: item.suggestedCategoryId,
      expectedLaunch: item.launch,
      idempotencyKey: newIdempotencyKey(),
      });
    },
    onSuccess: (response) => {
      toast(response.idempotentReplay ? 'A aprovação anterior foi recuperada.' : 'Movimentação aprovada e lançada.', 'success');
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setConfirm(null);
    },
    onError: (error) => toast(errorMessage(error), 'error'),
  });
  const approveSafe = useMutation({
    mutationFn: (reviewIds: string[]) => apiPost<ApproveOpenFinanceSafeResponse>('/open-finance/reviews/approve-safe', {
      reviewIds,
      idempotencyKey: newIdempotencyKey(),
    }),
    onSuccess: (response) => {
      toast(bulkResultMessage({
        approvedCount: response.summary.created,
        skippedCount: response.summary.skipped + response.summary.blocked,
        failedCount: response.summary.failed,
      }), response.summary.failed > 0 ? 'info' : 'success');
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setConfirm(null);
    },
    onError: (error) => toast(errorMessage(error), 'error'),
  });
  const edit = useMutation({
    mutationFn: ({ item, date, title, launch }: { item: QueueItem; date: Ymd; title: string; launch: OpenFinanceLaunch }) =>
      apiPatch<OpenFinanceReviewItem>(`/open-finance/reviews/${item.id}`, { revision: item.revision, date, title, launch }),
    onSuccess: () => {
      toast('Revisão salva. Confira o item antes de aprovar.', 'success');
      setEditItem(null);
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error) => toast(errorMessage(error), 'error'),
  });
  const ignore = useMutation({
    mutationFn: (item: QueueItem) => apiPost<{ ignored: true }>(`/open-finance/reviews/${item.id}/ignore`, { revision: item.revision }),
    onSuccess: () => {
      toast('Movimentação ignorada sem alterar suas finanças.', 'success');
      setIgnoreItem(null);
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error) => toast(errorMessage(error), 'error'),
  });
  const seen = useMutation({
    mutationFn: (item: QueueItem) => apiPost<{ seen: true }>(`/open-finance/reviews/${item.id}/seen`, { revision: item.revision }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
    onError: (error) => toast(errorMessage(error), 'error'),
  });
  const sync = useMutation({
    mutationFn: () => apiPost<SyncOpenFinanceResponse>('/open-finance/sync', {}),
    onMutate: () => { setSyncStartedAt(Date.now()); setSyncProgress(0); setSyncSummary(null); },
    onSuccess: (result) => {
      const failures = result.runs.filter((run) => run.status !== 'succeeded');
      const inserted = result.runs.reduce((sum, run) => sum + run.reviewItemsCreated, 0);
      const message = failures.length > 0
        ? failures[0]?.error ?? 'A sincronização foi parcial. Tente novamente.'
        : result.runs.length === 0 ? 'Nenhuma conta Open Finance ativa está mapeada.' : `${inserted} movimentação(ões) nova(s) para revisão.`;
      setSyncProgress(100);
      setSyncSummary(message);
      toast(message, failures.length > 0 ? 'error' : 'success');
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error) => { setSyncSummary(errorMessage(error)); toast(errorMessage(error), 'error'); },
    onSettled: () => setSyncStartedAt(null),
  });
  const groups = queue.data?.groups ?? [];
  const syncIssues = queue.data?.syncIssues ?? [];
  const safeIds = useMemo(() => safeReviewIds(groups), [groups]);
  const bulkReviewIds = safeIds.slice(0, 200);
  const safeCount = safeIds.length;

  return (
    <section className="space-y-5">
      <PageHeader
        title="Open Finance"
        subtitle="Revise as movimentações antes de elas entrarem nas suas finanças."
        actions={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            {safeCount > 0 && (
              <button
                type="button"
                onClick={() => setConfirm({ kind: 'bulk', reviewIds: bulkReviewIds, totalSafe: safeCount })}
                disabled={approveSafe.isPending || approve.isPending}
                className="flex h-11 items-center justify-center gap-2 rounded-full bg-primary px-4 text-sm font-semibold text-white transition-colors hover:bg-primary-hover disabled:opacity-60"
              >
                <Check className="size-4" /> Aprovar seguras ({bulkReviewIds.length})
              </button>
            )}
            <button
              type="button"
              onClick={() => sync.mutate()}
              disabled={sync.isPending}
              className="relative flex h-11 items-center justify-center gap-2 overflow-hidden rounded-full border border-border bg-surface px-4 text-sm font-semibold text-ink transition-colors hover:bg-canvas disabled:opacity-60"
            >
              {sync.isPending && <span aria-hidden className="absolute inset-y-0 left-0 bg-primary-weak transition-[width] duration-300" style={{ width: `${syncProgress}%` }} />}
              <span className="relative flex items-center gap-2">
                <RefreshCw className={cn('size-4', sync.isPending && 'animate-spin')} />
                {sync.isPending ? `Sincronizando · ${syncProgress}%` : 'Sincronizar agora'}
              </span>
            </button>
            <button
              type="button"
              onClick={() => void queue.refetch()}
              disabled={queue.isFetching}
              className="flex h-11 items-center justify-center gap-2 rounded-full border border-border bg-surface px-4 text-sm font-semibold text-ink transition-colors hover:bg-canvas disabled:opacity-60"
            >
              <RefreshCw className={cn('size-4', queue.isFetching && 'animate-spin')} /> Atualizar
            </button>
          </div>
        }
        meta={groups.length > 0 ? <p className="text-sm text-muted"><strong className="text-ink">{safeCount}</strong> item{safeCount === 1 ? '' : 's'} pronto{safeCount === 1 ? '' : 's'} para aprovação.</p> : undefined}
      />
      {syncSummary && <p role="status" className="rounded-lg border border-border bg-surface px-4 py-3 text-sm text-ink">{syncSummary}</p>}
      {syncIssues.length > 0 && <section role="alert" className="rounded-[var(--radius-panel)] border border-expense/30 bg-expense-weak p-4 text-sm text-ink">
        <div className="flex items-start gap-3">
          <CircleAlert className="mt-0.5 size-5 shrink-0 text-expense-text" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold">Falha na consulta Open Finance</p>
            <p className="mt-1 text-muted">A última tentativa destas fontes não foi concluída. O erro continuará visível até uma sincronização bem-sucedida.</p>
            <div className="mt-3 space-y-2">
              {syncIssues.map((issue) => <details key={issue.sourceId} className="rounded-lg border border-expense/20 bg-surface px-3 py-2">
                <summary className="cursor-pointer font-medium">{issue.institutionName} · {issue.sourceName}</summary>
                <pre className="mt-2 whitespace-pre-wrap break-words font-mono text-xs text-expense-text">{issue.error}</pre>
                <p className="mt-1 text-xs text-muted">{new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(issue.occurredAt))}</p>
              </details>)}
            </div>
          </div>
        </div>
      </section>}

      {queue.isLoading && <QueueSkeleton />}
      {queue.isError && (
        <EmptyState icon={CircleAlert} title="Não foi possível carregar a fila" description={errorMessage(queue.error)}>
          <button type="button" onClick={() => void queue.refetch()} className="h-11 rounded-full bg-primary px-5 text-sm font-semibold text-white">Tentar novamente</button>
        </EmptyState>
      )}
      {!queue.isLoading && !queue.isError && groups.length === 0 && (
        <EmptyState icon={ShieldCheck} title="Nenhuma movimentação para revisar" description="Quando uma instituição sincronizar novos dados, eles aparecerão aqui para sua confirmação." />
      )}
      {!queue.isLoading && !queue.isError && groups.length > 0 && (
        <div className="space-y-4">{groups.map((group) => <QueueGroupCard key={`${group.connection.id}:${group.source.id}`} group={group} onApprove={(item) => setConfirm({ kind: 'single', item })} onEdit={(item) => setEditItem({ item, source: group.source })} onIgnore={setIgnoreItem} onSeen={(item) => seen.mutate(item)} approvingId={approve.isPending && confirm?.kind === 'single' ? confirm.item.id : null} />)}</div>
      )}

      {editItem && <TransactionModal
        key={editItem.item.id}
        open
        type={editItem.item.launch?.mode === 'transfer' || editItem.item.specialType === 'transfer'
          ? 'transfer' : editItem.item.launch?.mode === 'income' ? 'income' : 'expense'}
        editing={null}
        presetAccountId={editItem.source.kind === 'account' ? editItem.source.mappedSourceId : null}
        isCard={editItem.source.kind === 'card'}
        presetCardId={editItem.source.kind === 'card' ? editItem.source.mappedSourceId : null}
        reviewDraft={{
          amountCents: editItem.item.amountCents,
          date: editItem.item.suggestedDate as Ymd,
          description: editItem.item.suggestedTitle,
          categoryId: editItem.item.suggestedCategoryId,
          accountId: editItem.item.launch?.mode === 'card_payment'
            ? editItem.item.launch.accountId
            : editItem.item.launch?.mode === 'income' || editItem.item.launch?.mode === 'expense'
              ? editItem.item.launch.accountId : editItem.source.kind === 'account' ? editItem.source.mappedSourceId : null,
          cardId: editItem.item.launch?.mode === 'card_payment'
            ? editItem.item.launch.cardId
            : editItem.item.launch?.mode === 'income' || editItem.item.launch?.mode === 'expense'
              ? editItem.item.launch.cardId : editItem.source.kind === 'card' ? editItem.source.mappedSourceId : null,
          launch: editItem.item.launch,
          suggestedMode: editItem.item.launch?.mode ?? editItem.item.specialType ?? (editItem.item.amountCents < 0 ? 'expense' : 'income'),
        }}
        reviewSaving={edit.isPending}
        onClose={() => !edit.isPending && setEditItem(null)}
        onReviewSave={({ date, title, launch }) => edit.mutate({ item: editItem.item, date, title, launch })}
      />}
      <ConfirmDialog
        open={ignoreItem !== null}
        title="Ignorar movimentação?"
        message={ignoreItem ? `A movimentação ${ignoreItem.originalDescription} sairá da fila sem gerar lançamento.` : ''}
        confirmLabel="Ignorar"
        loading={ignore.isPending}
        onCancel={() => !ignore.isPending && setIgnoreItem(null)}
        onConfirm={() => ignoreItem && ignore.mutate(ignoreItem)}
      />
      <ConfirmDialog
        open={confirm !== null}
        title={confirm?.kind === 'bulk' ? 'Aprovar movimentações seguras?' : 'Aprovar movimentação?'}
        message={confirm?.kind === 'bulk'
          ? `${confirm.reviewIds.length} ${confirm.reviewIds.length === 1 ? 'movimentação segura será lançada' : 'movimentações seguras serão lançadas'} sequencialmente. Itens que exigem revisão não entram nesta ação.${confirm.totalSafe > confirm.reviewIds.length ? ` ${confirm.totalSafe - confirm.reviewIds.length} item(ns) ficará(ão) para a próxima aprovação por limite de 200.` : ''}`
          : confirm ? `Será criado o lançamento de ${confirm.item.suggestedTitle} conforme o destino configurado na revisão.` : ''}
        confirmLabel={confirm?.kind === 'bulk' ? `Aprovar ${confirm.reviewIds.length}` : 'Aprovar'}
        danger={false}
        loading={approve.isPending || approveSafe.isPending}
        onCancel={() => !approve.isPending && !approveSafe.isPending && setConfirm(null)}
        onConfirm={() => {
          if (!confirm) return;
          if (confirm.kind === 'bulk') approveSafe.mutate(confirm.reviewIds);
          else approve.mutate(confirm.item);
        }}
      />
    </section>
  );
}
